/**
 * JourneyPinGate — a polite, blocking access-code prompt.
 *
 * Covers the full screen (nothing behind it renders — unlike BillingHoldOverlay, which blurs a
 * still-visible journey, no journey content has even been fetched yet at this point). Shown when
 * a journey has a PIN configured (opt-in — database/133_client_journey_access.sql / per-recipient
 * as of database/134_journey_recipients.sql) and the client hasn't already verified it on this
 * device (or their cached proof expired).
 *
 * `locked` shows a distinct, friendlier message (no PIN input at all — retrying makes no
 * difference until the cooldown clears) instead of implying the client's own PIN was wrong
 * ten times over.
 *
 * `onRequestReset` (2026-07, optional) — "Send me a new code": self-service, but ALWAYS emails
 * the fresh code to the recipient's own on-file address (api/journey-pin.js's requestReset
 * action) — never anywhere this screen could redirect it, which is what keeps it safe. Omitted
 * entirely for a legacy shared link (no per-person address to send a reset to).
 */
import { useState } from 'react'

export default function JourneyPinGate({ onSubmit, busy, error, locked, retryAt, onRequestReset }) {
  const [pin, setPin] = useState('')
  const [resetState, setResetState] = useState('idle')   // idle | sending | sent | error
  const [resetError, setResetError] = useState('')

  function handleSubmit(e) {
    e.preventDefault()
    const clean = pin.replace(/\D/g, '')
    if (clean.length !== 6 || busy) return
    onSubmit(clean)
  }

  async function handleRequestReset() {
    if (!onRequestReset || resetState === 'sending') return
    setResetState('sending'); setResetError('')
    try {
      await onRequestReset()
      setResetState('sent')
    } catch (ex) {
      setResetState('error')
      setResetError(
        ex.code === 'too_soon' ? "You've already requested one recently — check your inbox (and spam folder)."
        : ex.code === 'reset_unavailable' ? 'This link can’t self-reset — contact your team at Alder James for a new code.'
        : 'Could not send a new code — please try again in a moment.'
      )
    }
  }

  const retryText = (() => {
    if (!retryAt) return 'Please try again in a few minutes.'
    const mins = Math.max(1, Math.ceil((new Date(retryAt).getTime() - Date.now()) / 60000))
    return `Please try again in about ${mins} minute${mins === 1 ? '' : 's'}.`
  })()

  return (
    <div style={S.scrim}>
      <div style={S.card}>
        <div style={S.eyebrow}>Access code required</div>
        <h2 style={S.title}>{locked ? 'Too many attempts' : 'Enter your access code'}</h2>
        <hr style={S.rule} />
        {locked ? (
          <p style={S.body}>
            For security, this link has been temporarily locked after several incorrect codes. {retryText}
          </p>
        ) : (
          <>
            <p style={S.body}>
              This link is protected with a 6-digit access code, sent to you along with the link itself.
              Enter it below to continue.
            </p>
            <form onSubmit={handleSubmit}>
              <input
                type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus
                maxLength={6} value={pin}
                onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                style={S.input}
              />
              {error && <div style={S.error}>That code didn&rsquo;t match. Please try again.</div>}
              <button type="submit" disabled={pin.length !== 6 || busy} style={{ ...S.submitBtn, opacity: pin.length === 6 && !busy ? 1 : 0.45 }}>
                {busy ? 'Checking…' : 'Continue'}
              </button>
            </form>
            {onRequestReset && (
              <div style={{ marginTop: 18 }}>
                {resetState === 'sent' ? (
                  <p style={{ ...S.body, color: '#1D9E75', fontSize: 13, margin: 0 }}>
                    A new code is on its way to your email — check your inbox (and spam folder).
                  </p>
                ) : (
                  <button type="button" onClick={handleRequestReset} disabled={resetState === 'sending'} style={S.resetLink}>
                    {resetState === 'sending' ? 'Sending a new code…' : "Don't have your code? Send me a new one"}
                  </button>
                )}
                {resetState === 'error' && <div style={{ ...S.error, marginTop: 6 }}>{resetError}</div>}
              </div>
            )}
            <p style={{ ...S.body, color: '#7a9aaa', fontSize: 13, marginTop: 16 }}>
              Think this link shouldn&rsquo;t be active? Contact your team at Alder James.
            </p>
          </>
        )}
      </div>
    </div>
  )
}

const S = {
  scrim: {
    position: 'fixed', inset: 0, zIndex: 100,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    background: 'rgba(4,21,24,0.55)',
    backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  },
  card: {
    width: '100%', maxWidth: 480, textAlign: 'center',
    background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(10px)',
    border: '0.5px solid rgba(255,255,255,0.6)', borderRadius: 16,
    boxShadow: '0 24px 64px rgba(4,21,24,0.5)', padding: 'clamp(32px, 5vw, 52px)',
  },
  eyebrow: {
    fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 500,
    letterSpacing: '0.24em', textTransform: 'uppercase', color: '#6b8f95', marginBottom: 16,
  },
  title: {
    fontFamily: '"Montserrat", sans-serif', fontSize: 'clamp(20px, 2.6vw, 27px)',
    fontWeight: 300, letterSpacing: '0.03em', color: '#07272D', lineHeight: 1.3, margin: 0,
  },
  rule: { height: '0.5px', background: 'rgba(7,39,45,0.12)', border: 'none', margin: '24px auto', width: 60 },
  body: { fontFamily: '"DM Sans", sans-serif', fontSize: 15, lineHeight: 1.7, color: '#3a5260', margin: '0 0 12px' },
  input: {
    width: '100%', maxWidth: 220, margin: '10px auto 0', display: 'block', textAlign: 'center',
    fontFamily: '"DM Mono", monospace', fontSize: 26, letterSpacing: '0.3em', padding: '12px 10px 12px 14px',
    color: '#07272D', background: '#fff', border: '0.5px solid rgba(7,39,45,0.22)', borderRadius: 8,
    outline: 'none', boxSizing: 'border-box',
  },
  error: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#c0392b', marginTop: 10 },
  resetLink: { background: 'none', border: 'none', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#1a6fa8', textDecoration: 'underline', padding: 0 },
  submitBtn: {
    display: 'block', margin: '20px auto 0', fontFamily: '"DM Sans", sans-serif', fontSize: 14, fontWeight: 600,
    color: '#fff', background: '#07272D', border: 'none', borderRadius: 8, padding: '12px 32px', cursor: 'pointer',
  },
}
